import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);
  const cardsRef = useRef([]);
  const canvasRef = useRef(null);

  // Particle background canvas (same as About page)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };
    const initParticles = () => {
      particles = [];
      const particleCount = Math.min(100, Math.floor(window.innerWidth / 20));
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 1,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.2,
          alpha: Math.random() * 0.5 + 0.2,
        });
      }
    };
    const draw = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 217, 102, ${p.alpha})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });
      animationId = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', resize);
    resize();
    draw();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  // Scroll animations for service cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.2 }
    );
    cardsRef.current.forEach((card) => card && observer.observe(card));
    return () => observer.disconnect();
  }, []);

  const services = [
    {
      id: 1,
      title: 'Septic Tank Cleaning',
      icon: '🛠️',
      description: 'Professional removal of sludge and waste using high‑powered vacuum trucks. Prevents overflow and bad odors.',
      fullDescription: 'Our septic tank cleaning service uses state‑of‑the‑art vacuum trucks to safely remove accumulated sludge and scum. We ensure your tank is thoroughly cleaned to prevent backups, odors, and costly repairs. Recommended every 3–5 years depending on usage.',
      image: 'https://media.istockphoto.com/id/1497774805/photo/sewer-pumping-machine-septic-truck-pipe-in-the-drainage-pit-pumping-out-sewage-from-a-septic.jpg?s=612x612&w=0&k=20&c=mFFFNN3CETaPA0rGQ6K2bCoE76BB8KZOesgfBsUl-hg=',
      features: ['Vacuum truck extraction', 'Sludge removal', 'Odor prevention', 'Eco‑friendly disposal'],
      price: 'From ₹1000',
    },
    {
      id: 2,
      title: 'Septic Tank Pumping',
      icon: '🚽',
      description: 'Regular pumping to avoid blockages and keep your system working smoothly. Recommended every 3–5 years.',
      fullDescription: 'Routine pumping removes solid waste before it builds up and causes blockages. Our team provides scheduled pumping services that keep your system in peak condition, extending its lifespan and preventing emergencies.',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTfL98U0Y7sZNFzJXeW5v9JAogo01-E8Zh-Ug&s',
      features: ['Scheduled pumping', 'Blockage prevention', 'System longevity', 'Affordable plans'],
      price: 'From ₹1000',
    },
    {
      id: 3,
      title: 'Drain & Pipe Cleaning',
      icon: '🚿',
      description: 'High‑pressure water jetting removes clogs and restores slow drains. Fast, effective, and eco‑friendly.',
      fullDescription: 'Using high‑pressure water jetting, we clear even the toughest clogs without damaging pipes. This method is safe for your plumbing and restores flow instantly. Ideal for slow drains, recurring blockages, and grease buildup.',
      image: 'https://media.istockphoto.com/id/1412684227/photo/sewer-cleaning-service-worker-clean-a-clogged-drainage-with-hydro-jetting.jpg?s=612x612&w=0&k=20&c=9Y29Qld_zvlKYoe-uUc9wRQZ2td83aCH7rbN76GufXg=',
      features: ['High‑pressure jetting', 'Clog removal', 'Fast drainage', 'No chemicals'],
      price: 'From ₹600',
    },
    {
      id: 4,
      title: 'Septic Tank Inspection',
      icon: '🧪',
      description: 'Camera inspection to detect cracks, leaks, and potential failures. Detailed report with recommendations.',
      fullDescription: 'Our camera inspection service gives you a clear view inside your tank and pipes. We identify cracks, leaks, root intrusion, and other issues before they become expensive problems. A detailed report helps you plan maintenance or repairs.',
      image: 'https://d2nnm6ppct4rc3.cloudfront.net/septic_dye_inspection_cb9ca17d29.jpeg',
      features: ['Camera inspection', 'Leak detection', 'Maintenance planning', 'Digital report'],
      price: 'From ₹500',
    },
    {
      id: 5,
      title: 'Septic Tank Repair',
      icon: '🧱',
      description: 'Fix damaged tanks, pipes, and components. Emergency repairs available 24/7.',
      fullDescription: 'Whether it’s a cracked tank, broken pipe, or malfunctioning pump, our repair team responds quickly. We offer 24/7 emergency service to minimize disruption and protect your property.',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRG7hKQhmeeRUJ4ePS2nWPCwydYF5e-84g5LA&s',
      features: ['Repair services', 'Preventive maintenance', 'Emergency fixes', 'Warranty on work'],
      price: 'From ₹1500',
    },
    {
      id: 6,
      title: 'Waste Disposal',
      icon: '🚛',
      description: 'Safe, environmentally responsible transport and disposal of septic waste. Compliant with all regulations.',
      fullDescription: 'We handle the safe removal and disposal of septic waste at licensed facilities. Our process meets all environmental regulations, giving you peace of mind that your waste is handled responsibly.',
      image: 'https://t3.ftcdn.net/jpg/04/10/33/12/360_F_410331212_76fw08VV2HvzOat340jBovJRuwUDOsyL.jpg',
      features: ['Safe transport', 'Eco‑friendly disposal', 'Regulation compliance', 'Documentation available'],
      price:  ' From: ₹500 (Load: ₹500 + Chemical Charge: ₹70/liter)', // Updated price
    },
    {
      id: 7,
      title: 'Grease Trap Cleaning',
      icon: '🌿',
      description: 'Specialized cleaning for kitchen grease traps in restaurants and commercial kitchens. Prevents blockages and odors.',
      fullDescription: 'Our grease trap cleaning service is designed for restaurants, cafeterias, and commercial kitchens. We remove accumulated grease, fats, and oils to prevent foul odors and blockages in the drainage system.',
      image: 'https://zeve.au/bigblue/uploads/2024/06/how-to-clean-commercial-grease-trap.jpg',
      features: ['Grease removal', 'Kitchen/restaurant focus', 'Blockage prevention', 'Scheduled service'],
      price: 'From ₹500',
    },
    {
      id: 8,
      title: 'Water tank cleaning',
      icon: '🌿',
      description: 'Specialized cleaning for kitchen grease traps in restaurants and commercial kitchens. Prevents blockages and odors.',
      fullDescription: 'Our grease trap cleaning service is designed for restaurants, cafeterias, and commercial kitchens. We remove accumulated grease, fats, and oils to prevent foul odors and blockages in the drainage system.',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR2goxOY0snjK9FcKvRKxraVgC0oe_B0_VPvg&s',
      features: ['Grease removal', 'Kitchen/restaurant focus', 'Blockage prevention', 'Scheduled service'],
      price: 'From ₹1000',
    },
  ];

  const stats = [
    { number: '20+', label: 'Years Experience' },
    { number: '5000+', label: 'Happy Customers' },
    { number: '24/7', label: 'Emergency Service' },
    { number: '100%', label: 'Satisfaction Guarantee' },
  ];

  const deepBlue = '#0A2F44';
  const silver = '#F0F4F8';
  const gold = '#FFD966';

  const styles = {
    pageWrapper: {
      backgroundColor: deepBlue,
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden',
    },
    canvas: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 0,
    },
    content: {
      position: 'relative',
      zIndex: 1,
    },
    hero: {
      background: `linear-gradient(135deg, rgba(10,47,68,0.85), rgba(10,47,68,0.95)), url('https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      padding: '6rem 1rem',
      textAlign: 'center',
      color: '#FFFFFF',
    },
    heroContent: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    heroTitle: {
      fontSize: '2.8rem', // reduced from 3rem
      fontWeight: 'bold',
      marginBottom: '1rem',
      animation: 'fadeInUp 0.8s ease',
      textShadow: '0 0 8px rgba(240,244,248,0.3)',
    },
    heroSubtitle: {
      fontSize: '1.1rem', // reduced from 1.2rem
      marginBottom: '2rem',
      opacity: 0.9,
      color: silver,
    },
    heroButton: {
      display: 'inline-block',
      backgroundColor: gold,
      color: deepBlue,
      padding: '0.8rem 1.8rem',
      borderRadius: '50px',
      textDecoration: 'none',
      fontWeight: 'bold',
      transition: 'background 0.2s, transform 0.2s',
    },
    main: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '4rem 1rem',
    },
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '2rem',
    },
    serviceCard: {
      backgroundColor: 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(8px)',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      opacity: 0,
      transform: 'translateX(0)',
      border: '1px solid rgba(255,255,255,0.1)',
    },
    serviceImage: {
      width: '100%',
      height: '280px',
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    },
    serviceContent: {
      padding: '1.5rem',
    },
    serviceHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '0.75rem',
    },
    serviceIcon: {
      fontSize: '2rem',
      color: gold,
    },
    serviceTitle: {
      fontSize: '1.2rem', // reduced from 1.3rem
      fontWeight: 'bold',
      color: '#FFFFFF',
      margin: 0,
    },
    serviceDescription: {
      color: silver,
      marginBottom: '0.75rem',
      lineHeight: '1.5',
      fontSize: '0.95rem', // slightly reduced
    },
    servicePrice: {
      fontWeight: 'bold',
      color: gold,
      marginBottom: '1rem',
      fontSize: '0.95rem',
    },
    serviceActions: {
      marginTop: '0.5rem',
      display: 'flex',
      gap: '1rem',
      alignItems: 'center',
    },
    learnMoreLink: {
      color: gold,
      textDecoration: 'none',
      fontWeight: 'bold',
      fontSize: '0.9rem',
      cursor: 'pointer',
      background: 'none',
      border: 'none',
      padding: 0,
      marginLeft: '0.25rem',
      transition: 'color 0.2s',
    },
    contactButton: {
      display: 'inline-block',
      backgroundColor: gold,
      color: deepBlue,
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      textDecoration: 'none',
      fontWeight: 'bold',
      fontSize: '0.9rem',
      transition: 'background 0.2s, transform 0.2s',
      marginTop: '0.5rem',
    },
    callButton: {
      display: 'inline-block',
      backgroundColor: 'rgba(255,217,102,0.2)',
      color: gold,
      padding: '0.5rem 1rem',
      borderRadius: '8px',
      textDecoration: 'none',
      fontWeight: 'bold',
      fontSize: '0.9rem',
      transition: 'all 0.2s',
      marginTop: '0.5rem',
      border: `1px solid ${gold}`,
    },
    statsSection: {
      backgroundColor: 'rgba(0,0,0,0.3)',
      backdropFilter: 'blur(8px)',
      padding: '3rem 1rem',
      marginTop: '4rem',
    },
    statsGrid: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '2rem',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    statCard: {
      textAlign: 'center',
      color: '#FFFFFF',
    },
    statNumber: {
      fontSize: '1.8rem', // reduced from 2rem
      fontWeight: 'bold',
      marginBottom: '0.5rem',
      color: gold,
    },
    statLabel: {
      fontSize: '0.95rem', // reduced from 1rem
      opacity: 0.9,
      color: silver,
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
    },
    modalContent: {
      backgroundColor: '#0F3B4F',
      maxWidth: '600px',
      width: '100%',
      borderRadius: '16px',
      overflow: 'hidden',
      maxHeight: '90vh',
      overflowY: 'auto',
      border: '1px solid rgba(255,217,102,0.2)',
    },
    modalImage: {
      width: '100%',
      height: '250px',
      objectFit: 'cover',
    },
    modalBody: {
      padding: '1.5rem',
      color: silver,
      fontSize: '0.95rem',
    },
    closeButton: {
      float: 'right',
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: gold,
    },
    modalTitle: {
      fontSize: '1.6rem', // reduced from 1.8rem
      marginBottom: '1rem',
      color: '#FFFFFF',
    },
    modalPrice: {
      fontWeight: 'bold',
      color: gold,
      marginBottom: '1rem',
    },
    modalButton: {
      display: 'inline-block',
      backgroundColor: gold,
      color: deepBlue,
      padding: '0.6rem 1.2rem',
      borderRadius: '8px',
      textDecoration: 'none',
      fontWeight: 'bold',
      marginTop: '1rem',
      transition: 'background 0.2s, transform 0.2s',
    },
  };

  const animationStyles = `
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      background-color: ${deepBlue};
      margin: 0;
      padding: 0;
    }
    html {
      font-size: 90%;
    }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes slideInLeft {
      from { opacity: 0; transform: translateX(-50px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(50px); }
      to { opacity: 1; transform: translateX(0); }
    }
    .service-card {
      opacity: 0;
    }
    .service-card.visible {
      animation-duration: 0.6s;
      animation-fill-mode: forwards;
      animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    }
    .service-card.visible.left {
      animation-name: slideInLeft;
    }
    .service-card.visible.right {
      animation-name: slideInRight;
    }
    .service-card:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 24px rgba(0,0,0,0.3);
      background: rgba(255,255,255,0.1);
    }
    .service-card:hover .service-img {
      transform: scale(1.05);
    }
    .service-card:hover .learn-more {
      color: #FFFFFF;
    }
    .service-card:hover .contact-button {
      background-color: #FFFFFF;
      color: ${deepBlue};
    }
    .service-card:hover .call-button {
      background-color: ${gold};
      color: ${deepBlue};
    }
    .service-card .learn-more {
      transition: color 0.2s;
    }
    .service-card .contact-button, .service-card .call-button {
      transition: all 0.2s;
    }
  `;

  const getCardDirection = (index) => (index % 2 === 0 ? 'left' : 'right');

  const openModal = (service) => setSelectedService(service);
  const closeModal = () => setSelectedService(null);

  const renderDescription = (service) => {
    const maxLength = 80;
    const text = service.description;
    const isLong = text.length > maxLength;
    const shortText = isLong ? text.substring(0, maxLength) : text;

    return (
      <p style={styles.serviceDescription}>
        {shortText}
        {isLong && '... '}
        <button
          onClick={() => openModal(service)}
          className="learn-more"
          style={styles.learnMoreLink}
          onMouseEnter={(e) => (e.target.style.color = '#FFFFFF')}
          onMouseLeave={(e) => (e.target.style.color = gold)}
        >
          Learn More →
        </button>
      </p>
    );
  };

  return (
    <div style={styles.pageWrapper}>
      <style>{animationStyles}</style>
      <canvas ref={canvasRef} style={styles.canvas} />
      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            <h1 style={styles.heroTitle}>Professional Septic Services</h1>
            <p style={styles.heroSubtitle}>
              Reliable, eco‑friendly solutions for your septic system. From cleaning to repairs, we've got you covered.
            </p>
            <Link
              to="/contact"
              style={styles.heroButton}
              onMouseEnter={(e) => (e.target.style.backgroundColor = '#FFFFFF')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = gold)}
            >
              Get Free Estimate
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div style={styles.main}>
          <div style={styles.servicesGrid}>
            {services.map((service, index) => (
              <div
                key={service.id}
                ref={(el) => (cardsRef.current[index] = el)}
                className={`service-card ${getCardDirection(index)}`}
                style={styles.serviceCard}
              >
                <img
                  src={service.image}
                  alt={service.title}
                  className="service-img"
                  style={styles.serviceImage}
                />
                <div style={styles.serviceContent}>
                  <div style={styles.serviceHeader}>
                    <div style={styles.serviceIcon}>{service.icon}</div>
                    <h3 style={styles.serviceTitle}>{service.title}</h3>
                  </div>
                  {renderDescription(service)}
                  <div style={styles.servicePrice}>{service.price}</div>
                  <div style={styles.serviceActions}>
                    <Link
                      to="/contact"
                      className="contact-button"
                      style={styles.contactButton}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#FFFFFF';
                        e.target.style.color = deepBlue;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = gold;
                        e.target.style.color = deepBlue;
                      }}
                    >
                      Let's connect
                    </Link>
                    <a
                      href="tel:9948803051"
                      className="call-button"
                      style={styles.callButton}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = gold;
                        e.target.style.color = deepBlue;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(255,217,102,0.2)';
                        e.target.style.color = gold;
                      }}
                    >
                      📞 
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <div style={styles.statsSection}>
          <div style={styles.statsGrid}>
            {stats.map((stat, idx) => (
              <div key={idx} style={styles.statCard}>
                <div style={styles.statNumber}>{stat.number}</div>
                <div style={styles.statLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Modal for Service Details */}
        {selectedService && (
          <div style={styles.modal} onClick={closeModal}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <img src={selectedService.image} alt={selectedService.title} style={styles.modalImage} />
              <div style={styles.modalBody}>
                <button style={styles.closeButton} onClick={closeModal}>×</button>
                <h2 style={styles.modalTitle}>{selectedService.title}</h2>
                <p>{selectedService.fullDescription}</p>
                <ul>
                  {selectedService.features.map((feature, idx) => (
                    <li key={idx}>✓ {feature}</li>
                  ))}
                </ul>
                <p style={styles.modalPrice}>Price: {selectedService.price}</p>
                <Link
                  to="/contact"
                  style={styles.modalButton}
                  onClick={closeModal}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#FFFFFF';
                    e.target.style.color = deepBlue;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = gold;
                    e.target.style.color = deepBlue;
                  }}
                >
                  Request a Quote
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Services;